import { Controller } from "@hotwired/stimulus"

// Incremental inbox search. From the 3rd character it fetches server-rendered
// results (debounced) and swaps them in place of the live conversation list;
// clearing the query restores the list untouched, so the realtime inbox
// controller keeps its DOM and subscriptions.
export default class extends Controller {
  static targets = ["input", "textToggle", "results", "list"]
  static values = { url: String, status: String, mine: String, unread: String }

  static MIN_CHARS = 3
  static DEBOUNCE_MS = 250

  disconnect() {
    clearTimeout(this.timer)
    this.abortController?.abort()
  }

  queryChanged() {
    clearTimeout(this.timer)
    const query = this.inputTarget.value.trim()

    if (query.length < this.constructor.MIN_CHARS) {
      this.showList()
      return
    }

    this.timer = setTimeout(() => this.search(query), this.constructor.DEBOUNCE_MS)
  }

  async search(query) {
    this.abortController?.abort()
    this.abortController = new AbortController()

    const params = new URLSearchParams({ q: query })
    if (this.textToggleTarget.checked) params.set("text", "true")
    if (this.statusValue) params.set("status", this.statusValue)
    if (this.mineValue) params.set("mine", this.mineValue)
    if (this.unreadValue) params.set("unread", this.unreadValue)

    try {
      const response = await fetch(`${this.urlValue}?${params}`, {
        headers: { Accept: "text/html" },
        signal: this.abortController.signal
      })
      if (!response.ok) return

      // A slower earlier request must not clobber what the user typed since.
      if (this.inputTarget.value.trim() !== query) return

      this.resultsTarget.innerHTML = await response.text()
      this.resultsTarget.classList.remove("hidden")
      this.listTarget.classList.add("hidden")
    } catch (error) {
      if (error.name !== "AbortError") throw error
    }
  }

  showList() {
    this.abortController?.abort()
    this.resultsTarget.classList.add("hidden")
    this.resultsTarget.innerHTML = ""
    this.listTarget.classList.remove("hidden")
  }
}
